'use client';

import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { MyTicketRow } from '@/lib/services/support.service';
import { KIND_LABELS, STATUS_LABELS, STATUS_TONES, formatHour, formatDay } from '@/lib/support/labels';
import { SupportIllustration } from './SupportIllustration';
import { useSupportPanel } from './SupportProvider';

/** L'heure du jour, sinon la date */
const when = (iso: string) => {
  const d = new Date(iso);
  return formatDay(d) === formatDay(new Date()) ? formatHour(d) : formatDay(d).slice(0, 5);
};

/** « Mes demandes » (P06) : de la plus recente activite a la plus ancienne ; le point bleu d'une reponse non lue */
export function MyRequests() {
  const { setTab, setThread, isOpen, lastLive } = useSupportPanel();
  const [tickets, setTickets] = useState<MyTicketRow[] | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Relue a chaque ouverture, et quand une reponse arrive en direct
  useEffect(() => {
    if (!isOpen) return;
    let gone = false;
    setError(false);
    fetch('/api/support/tickets')
      .then(res => (res.ok ? res.json() as Promise<{ tickets: MyTicketRow[] }> : Promise.reject(new Error())))
      .then(data => { if (!gone) setTickets(data.tickets); })
      .catch(() => { if (!gone) setError(true); });
    return () => { gone = true; };
  }, [isOpen, attempt, lastLive]);

  return (
    <div className="grid min-h-0 flex-1 content-start gap-2 overflow-auto px-4 py-3">
      {error ? (
        <div role="alert" className="flex gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          <AlertTriangle size={15} className="mt-0.5 flex-none" />
          <span>Vos demandes n&apos;ont pas pu être chargées. <button type="button" onClick={() => setAttempt(n => n + 1)} className="font-semibold underline">Réessayer</button></span>
        </div>
      ) : tickets === null ? (
        [1, 2, 3].map(i => <div key={i} className="h-[58px] animate-pulse rounded-[10px] bg-[var(--ink-100)]" />)
      ) : tickets.length === 0 ? (
        <div className="grid justify-items-center gap-2 px-2.5 py-8 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-[var(--accent-50)]"><SupportIllustration size={44} /></span>
          <b>Aucune demande pour l&apos;instant</b>
          <span className="text-xs text-[var(--ink-500)]">Une question, un problème, une idée ? Écrivez-nous.</span>
          <button type="button" onClick={() => setTab('new')} className="rounded-[9px] bg-[var(--accent)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--accent-700)]">Nouvelle demande</button>
        </div>
      ) : (
        <ul className="m-0 grid list-none gap-1.5 p-0">
          {tickets.map(t => (
            <li key={t.number}>
              <button type="button" onClick={() => setThread(t.number)}
                className={`grid w-full grid-cols-[1fr_auto] gap-x-2 gap-y-1 rounded-[10px] border px-3 py-2 text-left hover:bg-[var(--ink-50)] ${t.unread ? 'border-[var(--accent-100)] bg-[var(--accent-50)]' : 'border-[var(--ink-200)]'}`}>
                <b className="truncate text-[12.5px] text-[var(--ink-900)]">{t.unread > 0 && <span className="text-[var(--accent)]">● </span>}{t.subject}</b>
                <span className="text-[11px] text-[var(--ink-500)]">{when(t.lastMessageAt)}</span>
                <span className="col-span-2 flex min-w-0 items-center gap-1.5 text-[11px] text-[var(--ink-500)]">
                  <span className={`flex-none rounded-full border px-1.5 text-[10px] font-semibold ${STATUS_TONES[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                  <span className="flex-none">{KIND_LABELS[t.kind]}</span>
                  <span className="truncate">· {t.lastMessage.author === 'ADMIN' ? 'Support' : 'Vous'} : {t.lastMessage.body}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
